<template>
  <AppCard
    v-if="is_visible"
    class="w-full cursor-pointer"
    :pt="{ body: 'p-3' }"
    data-test="whats-new-sidebar-card"
    @click="router.push(route_news_post)"
  >
    <template #content>
      <div class="flex flex-col gap-1">
        <div class="mb-1 flex items-center gap-2 text-primary-700">
          <AppIcon name="regular-bullhorn" />
          <AppText
            variant="eyebrow"
            class="text-primary-700"
          >
            {{ $t('whats_new.title') }}
          </AppText>
          <AppButton
            class="-my-1 -mr-2 ml-auto"
            :aria-label="$t('button.actions.close')"
            severity="secondary"
            variant="text"
            rounded
            size="small"
            data-test="whats-new-sidebar-card-close"
            @click.stop="dismiss"
          >
            <template #icon><AppIcon name="regular-xmark" /></template>
          </AppButton>
        </div>
        <AppText
          variant="body-strong"
          as="p"
          class="line-clamp-2 break-words"
        >
          {{ news_post_unread_latest.title }}
        </AppText>
        <AppText
          v-if="news_post_unread_latest.excerpt"
          variant="secondary"
          class="line-clamp-2 break-words"
        >
          {{ news_post_unread_latest.excerpt }}
        </AppText>
        <RouterLink
          v-slot="{ href, navigate }"
          custom
          :to="route_news_post"
        >
          <AppInternalLink
            class="text-sm"
            :to="href"
            data-test="whats-new-sidebar-card-read-more"
            @click.stop="navigate"
          >
            {{ $t('whats_new.read_more') }}
          </AppInternalLink>
        </RouterLink>
      </div>
    </template>
  </AppCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';
import AppButton from '@/app/shared_components/AppButton.vue';
import AppCard from '@/app/shared_components/AppCard.vue';
import AppInternalLink from '@/app/shared_components/AppInternalLink.vue';
import AppText from '@/app/shared_components/AppText.vue';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { mapGetters } from '@/store/lib/vue3helpers.js';
import { local_storage, LOCALSTORAGE_OPTIONS, localStorageKey } from '@/store/lib/local_storage';

const route = useRoute();
const router = useRouter();
const { current_profile, news_post_unread_latest } = mapGetters(['current_profile', 'news_post_unread_latest']);
const storage_key = localStorageKey(current_profile.value, LOCALSTORAGE_OPTIONS.WHATS_NEW_DISMISSED);

function readNewsPostIdDismissed() {
  if (!storage_key) return null;
  try {
    return local_storage.get(storage_key).news_post_id || null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
}

const news_post_id_dismissed = ref(readNewsPostIdDismissed());

const is_visible = computed(
  () =>
    Boolean(news_post_unread_latest.value) &&
    news_post_unread_latest.value.id !== news_post_id_dismissed.value &&
    route.name !== ROUTES_EMPLOYEES.CLIENT_WHATS_NEW,
);

const route_news_post = computed(() => ({
  name: ROUTES_EMPLOYEES.CLIENT_WHATS_NEW,
  hash: `#news-post-${news_post_unread_latest.value.id}`,
}));

function dismiss() {
  news_post_id_dismissed.value = news_post_unread_latest.value.id;
  if (!storage_key) return;
  try {
    local_storage.set(storage_key, { news_post_id: news_post_id_dismissed.value });
  } catch (error) {
    Sentry.captureException(error);
  }
}
</script>
